import React, { useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";

import {
  formatBytes,
  listImageLibraryItems,
  parseFirebaseStoragePathFromUrl,
  uploadCardImage,
} from "../../@shared/utils/cardImageStorage";

const getShortValueLabel = (url = "") => {
  const storagePath = parseFirebaseStoragePathFromUrl(url);
  if (storagePath) return storagePath;
  if (!url) return "";
  return url.length > 80 ? `${url.slice(0, 77)}...` : url;
};

const actionButtonSx = {
  textTransform: "none",
  borderRadius: 1,
  minHeight: 30,
  px: 1.25,
  fontWeight: 700,
};

export default function CardImagePickerField({
  label,
  value,
  onChange,
  storageFolder,
  disabled = false,
  helperText = "",
  showPreview = true,
}) {
  const fileInputRef = useRef(null);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [libraryLoading, setLibraryLoading] = useState(false);
  const [libraryError, setLibraryError] = useState("");
  const [libraryItems, setLibraryItems] = useState([]);
  const [libraryFolder, setLibraryFolder] = useState("");
  const [uploading, setUploading] = useState(false);
  const [selectedMeta, setSelectedMeta] = useState(null);
  const [statusMessage, setStatusMessage] = useState("");

  const selectedLabel = useMemo(
    () => selectedMeta?.fullPath || getShortValueLabel(value),
    [selectedMeta, value],
  );

  const loadLibrary = async ({ force = false } = {}) => {
    if (!storageFolder) return;
    if (!force && libraryItems.length && libraryFolder === storageFolder) return;

    setLibraryLoading(true);
    setLibraryError("");
    try {
      const items = await listImageLibraryItems(storageFolder);
      setLibraryItems(items);
      setLibraryFolder(storageFolder);
    } catch (error) {
      console.error("Failed to load card image library:", error);
      setLibraryItems([]);
      setLibraryError(`Failed to load /${storageFolder} images.`);
    } finally {
      setLibraryLoading(false);
    }
  };

  const handleOpenLibrary = async () => {
    setLibraryOpen(true);
    await loadLibrary();
  };

  const handleSelectLibraryImage = (item) => {
    setSelectedMeta(item);
    setStatusMessage("");
    onChange?.(item.url, item);
    setLibraryOpen(false);
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click?.();
  };

  const handleFileSelected = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !storageFolder) return;

    setUploading(true);
    setStatusMessage("");
    try {
      const result = await uploadCardImage({ file, folderPath: storageFolder });
      setSelectedMeta(result);
      onChange?.(result.url, result);
      setLibraryItems((prev) =>
        libraryFolder === storageFolder
          ? [{ name: result.name, fullPath: result.fullPath, url: result.url }, ...prev]
          : prev,
      );
      const sizeText =
        result.compressed && result.originalSize
          ? `Optimized ${formatBytes(result.originalSize)} to ${formatBytes(
              result.finalSize,
            )}.`
          : `Uploaded ${formatBytes(result.finalSize || file.size)}.`;
      setStatusMessage(result.warning || sizeText);
      if (result.warning) alert(result.warning);
    } catch (error) {
      console.error("Failed to upload card image:", error);
      alert("Failed to upload image. Check console for details.");
    } finally {
      setUploading(false);
      event.target.value = "";
    }
  };

  const handleClear = () => {
    setSelectedMeta(null);
    setStatusMessage("");
    onChange?.("", null);
  };

  const actionButtons = (
    <Stack direction="row" spacing={0.75} sx={{ flexWrap: "wrap" }}>
      <Button
        size="small"
        variant="contained"
        onClick={handleUploadClick}
        disabled={disabled || uploading || !storageFolder}
        sx={actionButtonSx}
      >
        {uploading ? "Uploading..." : "Upload"}
      </Button>
      <Button
        size="small"
        variant="outlined"
        onClick={handleOpenLibrary}
        disabled={disabled || uploading || !storageFolder}
        sx={actionButtonSx}
      >
        Library
      </Button>
      {value ? (
        <Button
          size="small"
          variant="text"
          onClick={handleClear}
          disabled={disabled || uploading}
          sx={{ ...actionButtonSx, color: "text.secondary" }}
        >
          Clear
        </Button>
      ) : null}
    </Stack>
  );

  return (
    <Box
      sx={{
        p: showPreview ? 1.25 : 1,
        borderRadius: 1,
        border: "1px solid #d9e1ec",
        background:
          "linear-gradient(180deg, rgba(248,250,252,0.98) 0%, rgba(241,245,249,0.98) 100%)",
      }}
    >
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25}>
        {showPreview && (
          <Box
            sx={{
              width: 88,
              height: 88,
              borderRadius: 1,
              border: "1px solid #d9e1ec",
              background: "#fff",
              overflow: "hidden",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            {value ? (
              <Box
                component="img"
                src={value}
                alt={label}
                sx={{ width: "100%", height: "100%", objectFit: "contain" }}
                onError={(event) => {
                  event.currentTarget.style.display = "none";
                }}
              />
            ) : (
              <Typography variant="caption" sx={{ opacity: 0.68 }}>
                No image
              </Typography>
            )}
          </Box>
        )}

        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Stack
            direction="row"
            spacing={0.75}
            alignItems="center"
            justifyContent="space-between"
            sx={{ gap: 0.75 }}
          >
            <Typography
              sx={{
                fontWeight: 900,
                fontSize: 13,
                color: "#172033",
                lineHeight: 1.2,
              }}
            >
              {label}
            </Typography>
            <Chip
              size="small"
              label={`/${storageFolder}`}
              sx={{
                maxWidth: showPreview ? 260 : 190,
                height: 22,
                borderRadius: 1,
                background: "#e8eef7",
                color: "#41516b",
                "& .MuiChip-label": {
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  px: 0.75,
                  fontSize: 10,
                  fontWeight: 700,
                },
              }}
            />
          </Stack>

          {selectedLabel ? (
            <Box
              sx={{
                mt: 0.75,
                p: 0.75,
                borderRadius: 1,
                border: "1px solid #e2e8f0",
                background: "#fff",
                minWidth: 0,
              }}
            >
              <Typography
                sx={{
                  color: "#64748b",
                  fontSize: 10,
                  fontWeight: 800,
                  textTransform: "uppercase",
                  letterSpacing: 0,
                }}
              >
                Selected
              </Typography>
              <Typography
                sx={{
                  color: "#172033",
                  fontFamily:
                    "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
                  fontSize: 11,
                  wordBreak: "break-word",
                  mt: 0.25,
                }}
              >
                {selectedLabel}
              </Typography>
            </Box>
          ) : (
            <Typography
              sx={{
                color: "#64748b",
                fontSize: 12,
                mt: 0.75,
              }}
            >
              Upload a new asset or choose one from storage.
            </Typography>
          )}

          {helperText ? (
            <Typography sx={{ color: "#64748b", fontSize: 11, mt: 0.5 }}>
              {helperText}
            </Typography>
          ) : null}

          {statusMessage ? (
            <Chip
              size="small"
              label={statusMessage}
              sx={{
                mt: 0.75,
                maxWidth: "100%",
                height: "auto",
                borderRadius: 1,
                background: "#edf7ed",
                color: "#245b2f",
                "& .MuiChip-label": { whiteSpace: "normal", py: 0.25 },
              }}
            />
          ) : null}

          <Box sx={{ mt: 1 }}>{actionButtons}</Box>
        </Box>
      </Stack>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={handleFileSelected}
      />

      <Dialog
        open={libraryOpen}
        onClose={() => setLibraryOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>{`Select ${label}`}</DialogTitle>
        <DialogContent dividers>
          <Chip
            size="small"
            label={`/${storageFolder}`}
            sx={{
              mb: 1.25,
              borderRadius: 1,
              background: "#e8eef7",
              color: "#41516b",
              fontWeight: 700,
            }}
          />

          {libraryLoading && (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={18} />
              <Typography>Loading images...</Typography>
            </Stack>
          )}

          {!libraryLoading && libraryError && (
            <Typography sx={{ color: "error.main" }}>{libraryError}</Typography>
          )}

          {!libraryLoading && !libraryError && !libraryItems.length && (
            <Typography>No images found in `/{storageFolder}`.</Typography>
          )}

          {!libraryLoading && !libraryError && libraryItems.length > 0 && (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "repeat(2, minmax(0, 1fr))",
                  sm: "repeat(3, minmax(0, 1fr))",
                  md: "repeat(4, minmax(0, 1fr))",
                },
                gap: 1,
              }}
            >
              {libraryItems.map((item) => (
                <Button
                  key={item.fullPath}
                  variant={value === item.url ? "contained" : "outlined"}
                  onClick={() => handleSelectLibraryImage(item)}
                  sx={{
                    p: 0.75,
                    textTransform: "none",
                    alignItems: "stretch",
                    justifyContent: "flex-start",
                    minHeight: 150,
                    borderRadius: 1,
                  }}
                >
                  <Stack spacing={0.6} sx={{ width: "100%" }}>
                    <Box
                      component="img"
                      src={item.url}
                      alt={item.name}
                      sx={{
                        width: "100%",
                        height: 90,
                        objectFit: "contain",
                        borderRadius: 1,
                        background: "rgba(0,0,0,0.08)",
                      }}
                    />
                    <Typography
                      sx={{
                        fontSize: 11,
                        lineHeight: 1.2,
                        textAlign: "left",
                        wordBreak: "break-word",
                      }}
                    >
                      {item.name}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 10,
                        opacity: 0.75,
                        textAlign: "left",
                        wordBreak: "break-word",
                      }}
                    >
                      {item.fullPath}
                    </Typography>
                  </Stack>
                </Button>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => loadLibrary({ force: true })} disabled={libraryLoading}>
            Refresh
          </Button>
          <Button onClick={() => setLibraryOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
